// src/pages/CourseDetailsPage/components/PreviewLessonPlayer.jsx
import React, { useState, useEffect, useCallback } from 'react';
import "../css/PreviewLessonPlayer.css";

const PreviewLessonPlayer = ({
    lessons = [],
    initialLesson = null,
    onLessonChange = null,
    autoPlay = true,
    className = ''
}) => {
    const [selectedLesson, setSelectedLesson] = useState(initialLesson || (lessons.length > 0 ? lessons[0] : null));
    const [showLessonList, setShowLessonList] = useState(true);
    const [videoError, setVideoError] = useState(false);

    useEffect(() => {
        if (!selectedLesson && lessons.length > 0) {
            const firstVideoLesson = lessons.find(l => l.video_file || l.video_url) || lessons[0];
            setSelectedLesson(firstVideoLesson);
            if (onLessonChange) {
                onLessonChange(firstVideoLesson);
            }
        }
    }, [lessons, onLessonChange, selectedLesson]);

    useEffect(() => {
        setVideoError(false);
    }, [selectedLesson]);

    const getVideoUrl = useCallback(() => {
        if (!selectedLesson) return null;

        let videoUrl = selectedLesson.video_file || selectedLesson.video_url || null;

        if (videoUrl) {
            if (videoUrl.startsWith('http://') || videoUrl.startsWith('https://')) {
                return videoUrl;
            }

            const baseUrl = process.env.REACT_APP_VIDEO_BASE_URL ||
                           process.env.REACT_APP_API_PROD_URL ||
                           'http://127.0.0.1:8000';
            const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            videoUrl = `${cleanBaseUrl}${videoUrl}`;
        }

        return videoUrl;
    }, [selectedLesson]);

    const formatDuration = (seconds) => {
        if (!seconds || seconds === 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleLessonSelect = useCallback((lesson) => {
        setSelectedLesson(lesson);
        setVideoError(false);
        if (onLessonChange) {
            onLessonChange(lesson);
        }
    }, [onLessonChange]);

    const handleVideoEnd = useCallback(() => {
        const currentIndex = lessons.findIndex(l => l.id === selectedLesson?.id);
        if (currentIndex < lessons.length - 1) {
            const nextLesson = lessons[currentIndex + 1];
            if (nextLesson.video_file || nextLesson.video_url) {
                handleLessonSelect(nextLesson);
            }
        }
    }, [lessons, selectedLesson, handleLessonSelect]);

    const toggleLessonList = useCallback(() => {
        setShowLessonList(!showLessonList);
    }, [showLessonList]);

    const handleVideoError = useCallback(() => {
        setVideoError(true);
    }, []);

    if (!selectedLesson && lessons.length === 0) {
        return (
            <div className="prev-vid-sec-empty">
                <i className="fas fa-video-slash"></i>
                <p>No preview lessons available</p>
            </div>
        );
    }

    const hasVideo = !!(selectedLesson?.video_file || selectedLesson?.video_url);
    const videoUrl = getVideoUrl();

    return (
        <div className={`prev-vid-sec ${className}`}>
            <div className="prev-vid-sec-wrapper">
                {/* Header */}
                <div className="prev-vid-sec-header">
                    <div className="prev-vid-sec-header-left">
                        <h2 className="prev-vid-sec-title">
                            <i className="fas fa-play-circle"></i>
                            Preview Lessons
                        </h2>
                        <span className="prev-vid-sec-lesson-count">
                            {lessons.length} lesson(s)
                        </span>
                    </div>
                    <div className="prev-vid-sec-header-right">
                        <button
                            className="prev-vid-sec-toggle-list-btn"
                            onClick={toggleLessonList}
                            title={showLessonList ? 'Hide lesson list' : 'Show lesson list'}
                        >
                            <i className={`fas fa-${showLessonList ? 'chevron-left' : 'chevron-right'}`}></i>
                            <span className="prev-vid-sec-toggle-list-label">
                                {showLessonList ? 'Hide preview Lessons' : 'View preview Lessons'}
                            </span>
                        </button>
                    </div>
                </div>

                <div className="prev-vid-sec-content">
                    {/* Video Player Section */}
                    <div className="prev-vid-sec-video-section">
                        {hasVideo && videoUrl ? (
                            <div className="prev-vid-sec-video-container">
                                {videoError ? (
                                    <div className="prev-vid-sec-video-error">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        <h3>Video Unavailable</h3>
                                        <p>Unable to load this video. Please try again later.</p>
                                        <p className="prev-vid-sec-error-url">URL: {videoUrl}</p>
                                        <button
                                            className="prev-vid-sec-retry-btn"
                                            onClick={() => {
                                                setVideoError(false);
                                                const videoElement = document.querySelector('.prev-vid-sec-video');
                                                if (videoElement) {
                                                    videoElement.load();
                                                }
                                            }}
                                        >
                                            <i className="fas fa-redo"></i> Retry
                                        </button>
                                    </div>
                                ) : (
                                    <video
                                        key={selectedLesson.id}
                                        className="prev-vid-sec-video"
                                        controls
                                        controlsList="nodownload noremoteplayback"
                                        disablePictureInPicture
                                        autoPlay={autoPlay}
                                        poster={selectedLesson.thumbnail_url}
                                        src={videoUrl}
                                        onEnded={handleVideoEnd}
                                        onError={handleVideoError}
                                        crossOrigin="anonymous"
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                )}
                            </div>
                        ) : (
                            <div className="prev-vid-sec-no-video">
                                <i className="fas fa-file-alt"></i>
                                <h3>{selectedLesson?.title || 'No lesson selected'}</h3>
                                <p>This lesson does not have a video</p>
                            </div>
                        )}

                        {/* Lesson Info */}
                        {selectedLesson && (
                            <div className="prev-vid-sec-lesson-info">
                                <div className="prev-vid-sec-lesson-info-header">
                                    <h3 className="prev-vid-sec-lesson-info-title">{selectedLesson.title}</h3>
                                </div>

                                {selectedLesson.subsection_title && (
                                    <div className="prev-vid-sec-lesson-subsection">
                                        <i className="fas fa-folder-open"></i>
                                        <span>{selectedLesson.subsection_title}</span>
                                    </div>
                                )}

                                {selectedLesson.content && hasVideo && (
                                    <p className="prev-vid-sec-lesson-info-description">{selectedLesson.content}</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Lesson List Sidebar */}
                    {showLessonList && (
                        <div className="prev-vid-sec-list-section">
                            <div className="prev-vid-sec-list-header">
                                <h3>All Preview Lessons</h3>
                                <span className="prev-vid-sec-list-count">{lessons.length}</span>
                            </div>

                            <div className="prev-vid-sec-list">
                                {lessons.map((lesson, index) => {
                                    const isSelected = selectedLesson?.id === lesson.id;
                                    const hasVideoLesson = !!(lesson.video_file || lesson.video_url);

                                    return (
                                        <div
                                            key={lesson.id}
                                            className={`prev-vid-sec-list-item ${isSelected ? 'is-selected' : ''} ${hasVideoLesson ? 'has-video' : ''}`}
                                            onClick={() => handleLessonSelect(lesson)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    handleLessonSelect(lesson);
                                                }
                                            }}
                                        >
                                            <div className="prev-vid-sec-list-number">{index + 1}</div>
                                            <div className="prev-vid-sec-list-content">
                                                <div className="prev-vid-sec-list-title">{lesson.title}</div>
                                                {lesson.subsection_title && (
                                                    <div className="prev-vid-sec-list-subsection">
                                                        <i className="fas fa-folder"></i>
                                                        <span>{lesson.subsection_title}</span>
                                                    </div>
                                                )}
                                                <div className="prev-vid-sec-list-meta">
                                                    <span className="prev-vid-sec-list-type">{lesson.lesson_type || 'Lesson'}</span>
                                                    {hasVideoLesson && (
                                                        <span className="prev-vid-sec-list-video-icon">
                                                            <i className="fas fa-video"></i>
                                                        </span>
                                                    )}
                                                    {lesson.video_duration > 0 && (
                                                        <span className="prev-vid-sec-list-duration">
                                                            {formatDuration(lesson.video_duration)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <div className="prev-vid-sec-list-check">
                                                    <i className="fas fa-check-circle"></i>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PreviewLessonPlayer;